import type { Client } from "stoat.js";

/**
 * Server resource limits + usage, as returned by `GET /servers/:id/limits`.
 *
 * The Censer billing backend currently returns caps and current usage wrapped
 * in `{ limits: {...} }`. Newer backends may additionally return explicit
 * over-capacity flags and/or an upgrade link; we prefer those when present and
 * otherwise derive over-capacity from caps + usage.
 */
export interface ServerLimits {
  maxMessages?: number | null;
  maxStorage?: number;
  storageUsed?: number;
  messageCount?: number;

  // Optional explicit flags (forward-compatible with the operator design spec)
  overCapacity?: boolean;
  messagesOverCapacity?: boolean;
  storageOverCapacity?: boolean;

  // Optional server-provided upgrade/checkout link (forward-compatible)
  upgradeUrl?: string;
  checkoutUrl?: string;
}

export interface OverCapacity {
  overCapacity: boolean;
  messagesOverCapacity: boolean;
  storageOverCapacity: boolean;
}

const NONE: OverCapacity = {
  overCapacity: false,
  messagesOverCapacity: false,
  storageOverCapacity: false,
};

/**
 * Fetch server limits.
 *
 * Fail-safe: returns `null` on any error (network, auth, non-2xx, malformed
 * body) so callers never break the client if the endpoint is unavailable or
 * the shape is unexpected.
 *
 * @param client Authenticated client
 * @param serverId Target server id
 */
export async function fetchServerLimits(
  client: Client,
  serverId: string,
): Promise<ServerLimits | null> {
  try {
    const [header, value] = client.authenticationHeader;
    const base = client.options.baseURL.replace(/\/+$/, "");
    const response = await fetch(`${base}/servers/${serverId}/limits`, {
      headers: { [header]: value, Accept: "application/json" },
    });

    if (!response.ok) return null;

    const data = (await response.json()) as
      | { limits?: ServerLimits }
      | ServerLimits
      | undefined;

    // The endpoint wraps its payload in `{ limits: {...} }`; tolerate both.
    const limits = ((data as { limits?: ServerLimits })?.limits ?? data) as
      | ServerLimits
      | undefined;

    if (!limits || typeof limits !== "object") return null;
    return limits;
  } catch {
    return null;
  }
}

/**
 * Determine whether a server is over capacity.
 *
 * Prefers explicit backend flags when present, otherwise derives them from
 * caps + usage. Fail-safe: unknown / missing data resolves to "not over
 * capacity".
 *
 * @param limits Limits payload (or null)
 */
export function evaluateOverCapacity(
  limits: ServerLimits | null | undefined,
): OverCapacity {
  if (!limits) return NONE;

  // Prefer explicit flags if the backend provides any of them.
  if (
    typeof limits.overCapacity === "boolean" ||
    typeof limits.messagesOverCapacity === "boolean" ||
    typeof limits.storageOverCapacity === "boolean"
  ) {
    const messagesOverCapacity = !!limits.messagesOverCapacity;
    const storageOverCapacity = !!limits.storageOverCapacity;
    return {
      messagesOverCapacity,
      storageOverCapacity,
      overCapacity:
        !!limits.overCapacity || messagesOverCapacity || storageOverCapacity,
    };
  }

  // Otherwise derive from caps + usage. `maxMessages` is null for unlimited
  // tiers, in which case the message cap can never be reached.
  const messagesOverCapacity =
    typeof limits.maxMessages === "number" &&
    typeof limits.messageCount === "number" &&
    limits.messageCount >= limits.maxMessages;

  const storageOverCapacity =
    typeof limits.maxStorage === "number" &&
    typeof limits.storageUsed === "number" &&
    limits.storageUsed >= limits.maxStorage;

  return {
    messagesOverCapacity,
    storageOverCapacity,
    overCapacity: messagesOverCapacity || storageOverCapacity,
  };
}

/**
 * Resolve a server-provided upgrade/checkout link, if any.
 *
 * We never invent a URL: this only returns a link the backend supplied. The
 * caller is responsible for any configured fallback.
 */
export function serverProvidedUpgradeUrl(
  limits: ServerLimits | null | undefined,
): string | undefined {
  return limits?.upgradeUrl || limits?.checkoutUrl || undefined;
}

/**
 * Whether a thrown send error could indicate an exceeded server quota.
 *
 * The current billing backend enforces the per-tier message/storage caps by
 * returning `FileTooLarge`; a newer backend may return `MessageQuotaReached`.
 * `stoat-api` throws the raw (string) response body on non-2xx, so we tolerate
 * both a JSON string and an already-parsed error envelope.
 *
 * Note: `FileTooLarge` is ambiguous (it is also the genuine oversize-file
 * error), so callers MUST confirm with {@link evaluateOverCapacity} before
 * showing a quota-specific message.
 */
export function isPossibleQuotaError(error: unknown): boolean {
  let parsed: unknown = error;
  if (typeof error === "string") {
    try {
      parsed = JSON.parse(error);
    } catch {
      return false;
    }
  }

  const type = (parsed as { type?: unknown } | null | undefined)?.type;
  return type === "MessageQuotaReached" || type === "FileTooLarge";
}
